/** Plain words for a run on a live dataset (`pull` set): its title, period, counts and failures. */

import type { ExperimentSummary, TraceCounts } from "../types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Scored straight from pulled traces, no agent. An agent run on fresh traces also has `pull`. */
export const isRuleRun = (e: Pick<ExperimentSummary, "pull" | "runner">): boolean =>
  !!e.pull && e.runner !== "agent";

/** "1–7 Oct", "28 Sep – 3 Oct", or "" when there is no period. */
export function formatPeriod(period: { start: string; end: string } | null | undefined): string {
  if (!period) return "";
  const a = new Date(period.start);
  const b = new Date(period.end);
  const day = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  if (a.getFullYear() !== b.getFullYear())
    return `${day(a)} ${a.getFullYear()} – ${day(b)} ${b.getFullYear()}`;
  if (a.getMonth() === b.getMonth()) return a.getDate() === b.getDate() ? day(a) : `${a.getDate()}–${day(b)}`;
  return `${day(a)} – ${day(b)}`;
}

/** The experiment's own name (a live pull is named by whoever started it). */
export function traceRunTitle(e: Pick<ExperimentSummary, "name">): string {
  return e.name;
}

export function traceCounts(e: Pick<ExperimentSummary, "trace_counts">): TraceCounts | null {
  const t = e.trace_counts;
  if (t.matched === undefined) return null;
  return { ...t, matched: t.matched, picked: t.picked ?? 0, already_scored: 0 };
}

/** The code a trace run failed with ("auth", "timeout"…), if the source could not be read. */
export function sourceErrorCode(e: Pick<ExperimentSummary, "trace_counts">): string | null {
  return e.trace_counts.source_error ?? null;
}

/** Why the source could not be read, naming it: Langfuse or qFlow (from the run's frozen connection). */
export function sourceErrorText(code: string | null | undefined, sourceType?: string): string {
  const qflow = sourceType === "qflow";
  const name = qflow ? "qFlow" : "Langfuse";
  if (code === "auth")
    return qflow
      ? "qFlow refused the workbench's sign-in. Check the connection's auth broker URL and region, then duplicate the run."
      : "Langfuse refused the keys. Check the connection in Settings.";
  // The run page's "Duplicate" is how to try again: a failed pull has nothing to rerun.
  if (code === "rate_limited") return `${name} is rate limiting us. Duplicate the run in a few minutes.`;
  if (code === "unreachable" || code === "timeout")
    return `Couldn't reach ${name}. Duplicate the run to try again.`;
  return `${name} could not be read. Duplicate the run to try again.`;
}

const FETCH_REASONS: Record<string, string> = {
  unreachable: "Langfuse did not answer",
  timeout: "Langfuse did not answer",
  rate_limited: "rate-limited",
  not_found: "gone from Langfuse",
};

/** "1 trace couldn't be fetched (Langfuse did not answer): trace-0003" or "3 … (2 rate-limited, 1 gone from Langfuse)", or "" when every picked trace came in. */
export function fetchSkipText(e: Pick<ExperimentSummary, "trace_counts">): string {
  const n = e.trace_counts.skipped ?? 0;
  if (n <= 0) return "";
  const found = Object.entries(e.trace_counts.by_reason ?? {}).filter(([, c]) => c > 0);
  const label = (r: string) => FETCH_REASONS[r] ?? r.replace(/_/g, " ");
  // One reason needs no count; several say how many each.
  const reasons =
    found.length === 1 ? found.map(([r]) => label(r)) : found.map(([r, c]) => `${c} ${label(r)}`);
  const why = reasons.length ? ` (${reasons.join(", ")})` : "";
  const ids = (e.trace_counts.examples ?? []).map((x) => x.trace_id).slice(0, 3);
  return `${n} ${n === 1 ? "trace" : "traces"} couldn't be fetched${why}${ids.length ? `: ${ids.join(", ")}` : ""}`;
}

/**
 * "1 trace couldn't be fetched (…) · 20 already scored · 3 not scored". The first never reached the dataset;
 * the last were imported but their checks did not run. Imported and scored counts are in the run summary.
 */
export function traceChips(e: ExperimentSummary): string[] {
  const t = traceCounts(e);
  const notScored = e.counts.not_run + e.counts.agent_error;
  return [
    fetchSkipText(e),
    t && t.already_scored > 0 ? `${t.already_scored} already scored` : "",
    notScored > 0 ? `${notScored} not scored` : "",
  ].filter(Boolean);
}
